<template>
  <div class="flex-grow-1" data-tourid="challenge-addon-component">
    <ChallengeSelector
      class="mb-3"
      v-if="canEdit && isEmptyChallenge"
      data-testid="challenge-addon-selector"
      :organizationId="organizationId"
      :location="location"
      @onChallengeSelection="onChallengeSelection"
    />

    <FieldLabel v-if="!isEmptyChallenge" :text="$t('common.challenge_one')" />

    <router-link
      v-if="!isEmptyChallenge"
      class="challenge-addon__meta text-decoration-none"
      data-testid="challenge-addon-meta"
      :to="`/challenge/${challenge.organization}/${challenge._id}`"
      :event="!canEdit ? 'click' : ''"
      :class="{ 'challenge-addon__meta--disabled': canEdit }"
    >
      <div class="challenge-addon__image-wrapper">
        <div
          class="challenge-addon__image"
          data-testid="challenge-addon-image"
          :style="{
            'background-image': `url('${challengeImage}')`,
          }"
        ></div>
      </div>
      <div class="challenge-addon__content">
        <p
          class="challenge-addon__content-duration"
          data-testid="challenge-addon-duration"
        >
          {{ challengeTimeRemaining }}
        </p>
        <p
          data-private
          class="challenge-addon__content-name"
          data-testid="challenge-addon-name"
        >
          {{ challenge.name }}
        </p>
      </div>
      <Button
        v-if="canEdit"
        color="grey"
        icon="mdi-close-circle-outline"
        class="challenge-addon__content-delete mt-n2"
        data-testid="challenge-addon-delete"
        @click="showDeleteModal = true"
      />
    </router-link>
    <DeleteConfirmationModal
      confirmTestID="challenge-addon-delete-button"
      :show="showDeleteModal"
      :isLoading="isDeleting"
      :title="$t('challenge.remove_challenge_question')"
      :text="confirmationText"
      @cancel="showDeleteModal = false"
      @delete="removeChallenge"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import _ from 'lodash'
import moment from 'moment'

import FieldLabel from '@/components/Core/FieldLabel'
import ChallengeSelector from '@/components/Rev/ChallengeSelector.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'ChallengeAddon',

  mixins: [mixin],

  components: {
    FieldLabel,
    ChallengeSelector,
    DeleteConfirmationModal,
    Button,
  },

  props: {
    canEdit: {
      type: Boolean,
      required: true,
      default: false,
    },
    revType: {
      type: String,
      required: false,
    },
    revId: {
      type: String,
      required: true,
      default: '',
    },
    location: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      challenge: {},
      isDeleting: false,
      showDeleteModal: false,
    }
  },

  async mounted() {
    this.challenge = await this.getRevChallenge()
  },

  computed: {
    ...mapGetters(['organizationId']),

    isEmptyChallenge() {
      return _.isEmpty(this.challenge)
    },

    challengeImage() {
      return this.challenge?.photo?.localMediumPath
    },

    challengeTimeRemaining() {
      const startAt = this.challenge?.startAt
      const endAt = this.challenge?.endAt
      return this.getChallengeTimeRemaining(startAt, endAt)
    },

    revTypeForRequest() {
      let revType = _.kebabCase(this.revType)
      if (revType === 'improvement-idea') revType = 'revs'
      return revType
    },

    confirmationText() {
      const startAt = this.challenge?.startAt
      const endAt = this.challenge?.endAt
      return this.isChallengeActive(startAt, endAt)
        ? this.$t('challenge.remove_challenge_confirmation')
        : this.$t('challenge.remove_closed_challenge_confirmation')
    },
  },

  methods: {
    ...mapActions({
      getChallengesFromRev: 'getChallengesFromRev',
      linkChallengeToRev: 'linkChallengeToRev',
      removeChallengeFromRev: 'removeChallengeFromRev',
    }),

    getRevChallenge() {
      return this.getChallengesFromRev({
        revType: this.revTypeForRequest,
        organizationId: this.organizationId,
        revId: this.revId,
      })
    },

    isChallengeActive(startAt, endAt) {
      const now = new Date().toISOString()
      return startAt <= now && endAt >= now
    },

    isChallengeComingSoon(startAt) {
      const now = new Date().toISOString()
      return startAt > now
    },

    getChallengeStatus(startAt, endAt) {
      if (this.isChallengeActive(startAt, endAt)) {
        return 'active'
      }
      if (this.isChallengeComingSoon(startAt)) {
        return 'soon'
      }
      return 'closed'
    },

    getChallengeTimeRemaining(startAt, endAt) {
      if (this.isEmptyChallenge) return ''
      const status = this.getChallengeStatus(startAt, endAt)
      if (status === 'active') {
        const time = Date.parse(endAt) - Date.parse(new Date())
        const days = Math.floor(time / (1000 * 60 * 60 * 24))
        return this.$t('common.ends_in_dayWithCount', {
          count: days,
        }).toUpperCase()
      }
      if (status === 'soon') {
        const time = Date.parse(startAt) - Date.parse(new Date())
        const days = Math.floor(time / (1000 * 60 * 60 * 24))
        return this.$t('common.starts_in_dayWithCount', { count: days })
      }
      if (status === 'closed') {
        return `${this.$t('common.finished')}: ${moment(endAt).format(
          'MMM D, YYYY'
        )}`
      }
    },

    async onChallengeSelection(challengeId) {
      await this.linkChallengeToRev({
        organizationId: this.organizationId,
        revType: this.revTypeForRequest,
        revId: this.revId,
        challengeId,
      })
      this.challenge = await this.getRevChallenge()
      this.$emit('onChallengeChange')
    },

    async removeChallenge() {
      try {
        this.isDeleting = true
        await this.removeChallengeFromRev({
          organizationId: this.organizationId,
          revType: this.revTypeForRequest,
          revId: this.revId,
        })

        this.challenge = {}
        this.showDeleteModal = false
        this.$emit('onChallengeChange')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.challenge-addon {
  &__meta {
    display: flex;
    flex-direction: grid;
    margin-bottom: 24px;

    &--disabled {
      cursor: default;
    }
  }

  &__image-wrapper {
    width: 72px;
    height: 72px;
    min-width: 72px;
    min-height: 72px;
    overflow: hidden;
  }

  &__image {
    width: 72px;
    height: 72px;
    background-position: center;
    background-size: cover;
    transform: scale(2);
  }

  &__content {
    padding-left: 16px;
    flex-grow: 2;

    &-duration {
      font-size: 10px;
      letter-spacing: 1.5px;
      line-height: 16px;
      color: $color-blue-grey-darken3;
    }

    &-name {
      font-size: 13px;
      line-height: 18px;
      color: $color-blue-grey-darken3;
    }

    &-delete {
      align-self: flex-start;
      cursor: pointer;
    }
  }
}
</style>
